const REORDER_SECTION_ID = 'reorder-section-rendering';
const REORDER_SECTION_CONTAINER_ID =
  'shopify-section-reorder-section-rendering';
const REORDER_ERROR_MESSAGE =
  'We could not load reorder details. Please try again.';
const REORDER_CART_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const REORDER_NO_ITEMS_MESSAGE = 'Please select at least one item to reorder.';

let isReorderRequestInFlight = false;

async function handleReorderButtonClick(trigger) {
  if (isReorderRequestInFlight) {
    return;
  }

  const dialogModal = document.getElementById('DialogModal');

  if (
    !dialogModal ||
    typeof dialogModal.openModal !== 'function' ||
    typeof dialogModal.setLoading !== 'function' ||
    typeof dialogModal.setContent !== 'function' ||
    typeof dialogModal.setSize !== 'function'
  ) {
    return;
  }

  isReorderRequestInFlight = true;
  trigger.classList.add('is-loading');

  // dialogModal.setSize('small');
  if (!dialogModal.open) {
    dialogModal.openModal();
  }
  dialogModal.setLoading();

  try {
    const requestUrl = new URL(window.location.origin);
    requestUrl.searchParams.set('section_id', REORDER_SECTION_ID);

    const response = await fetch(requestUrl.toString(), {
      method: 'GET',
      headers: {
        Accept: 'text/html',
      },
    });

    if (!response.ok) {
      throw new Error(`Reorder request failed: ${response.status}`);
    }

    const responseText = await response.text();
    const parsedHtml = new DOMParser().parseFromString(
      responseText,
      'text/html',
    );
    const sectionContent = parsedHtml.getElementById(
      REORDER_SECTION_CONTAINER_ID,
    );
    const sectionMarkup = sectionContent?.innerHTML?.trim() || '';

    if (!sectionMarkup) {
      throw new Error('Reorder section markup is empty.');
    }

    dialogModal.setContent(sectionMarkup);

    if (typeof window.wakaSyncReorderCookie === 'function') {
      await window.wakaSyncReorderCookie();
    }
  } catch (_error) {
    dialogModal.setContent(
      `<div class="p-6 text-center">${REORDER_ERROR_MESSAGE}</div>`,
    );
  } finally {
    isReorderRequestInFlight = false;
    trigger.classList.remove('is-loading');
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) {
    return;
  }

  const reorderButton = event.target.closest('.reorder-button');

  if (!reorderButton) {
    return;
  }

  const parentDrawer = reorderButton.closest('menu-drawer');
  if (parentDrawer && typeof parentDrawer.close === 'function') {
    parentDrawer.close();
  }

  event.preventDefault();
  handleReorderButtonClick(reorderButton);
});

class ReorderProducts extends HTMLElement {
  connectedCallback() {
    this.itemArticles = [];
    this.itemCheckboxes = [];
    this.selectAllCheckbox = this.querySelector(
      'input[name^="reorder_select_all_"]',
    );
    this.totalElement = this.querySelector('[data-reorder-total]');
    this.errorElement = this.querySelector('[data-reorder-error]');
    this.submitButton = this.querySelector('.reorder-submit');
    this.partialConfirmMessage =
      this.dataset.partialConfirmMessage ||
      'Not all items are selected. Continue with reorder?';
    this.isSubmitting = false;
    this.articleClickHandlers = [];

    this.handleItemChange = this.handleItemChange.bind(this);
    this.handleSelectAllChange = this.handleSelectAllChange.bind(this);
    this.handleSubmitClick = this.handleSubmitClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleItemsUpdated = this.handleItemsUpdated.bind(this);

    this.selectAllCheckbox?.addEventListener(
      'change',
      this.handleSelectAllChange,
    );
    this.submitButton?.addEventListener('click', this.handleSubmitClick);
    this.addEventListener('change', this.handleInputChange);
    this.addEventListener('reorder:items-updated', this.handleItemsUpdated);

    this.refreshItems();
  }

  disconnectedCallback() {
    this.teardownItemBindings();
    this.selectAllCheckbox?.removeEventListener(
      'change',
      this.handleSelectAllChange,
    );
    this.submitButton?.removeEventListener('click', this.handleSubmitClick);
    this.removeEventListener('change', this.handleInputChange);
    this.removeEventListener('reorder:items-updated', this.handleItemsUpdated);
  }

  refreshItems() {
    this.teardownItemBindings();

    this.itemArticles = Array.from(
      this.querySelectorAll('[data-reorder-item]'),
    );
    this.itemCheckboxes = this.itemArticles
      .map((article) => article.querySelector('[data-reorder-item-checkbox]'))
      .filter(Boolean);
    this.articleClickHandlers = [];

    this.itemCheckboxes.forEach((cb) =>
      cb.addEventListener('change', this.handleItemChange),
    );
    this.itemArticles.forEach((article) => {
      const handler = (event) => this.handleArticleClick(event, article);
      this.articleClickHandlers.push({ article, handler });
      article.addEventListener('click', handler);
    });

    this.initializeArticleVariants();
    this.syncAllQuantities();
    this.updateAllLinePrices();
    this.applyItemVisualStates();
    this.updateTotal();
    this.updateSelectAll();
    this.updateSubmitState();
  }

  teardownItemBindings() {
    this.itemCheckboxes?.forEach((cb) =>
      cb.removeEventListener('change', this.handleItemChange),
    );
    this.articleClickHandlers?.forEach(({ article, handler }) => {
      article.removeEventListener('click', handler);
    });
  }

  handleItemsUpdated() {
    this.refreshItems();
  }

  getSelectedArticles() {
    return this.itemArticles.filter((article) => {
      const cb = article.querySelector('[data-reorder-item-checkbox]');
      return cb?.checked;
    });
  }

  updateTotal() {
    const total = this.getSelectedArticles().reduce((sum, article) => {
      const price = Number(article.dataset.price) || 0;
      const quantity = this.getArticleQuantity(article);
      return sum + price * quantity;
    }, 0);

    if (this.totalElement) {
      this.totalElement.textContent = this.formatMoney(total);
    }
  }

  updateSelectAll() {
    if (!this.selectAllCheckbox) return;

    const checkedCount = this.itemCheckboxes.filter((cb) => cb.checked).length;
    const totalCount = this.itemCheckboxes.length;

    this.selectAllCheckbox.checked =
      totalCount > 0 && checkedCount === totalCount;
    this.selectAllCheckbox.indeterminate =
      checkedCount > 0 && checkedCount < totalCount;
  }

  handleItemChange() {
    this.applyItemVisualStates();
    this.updateTotal();
    this.updateSelectAll();
    this.updateSubmitState();
  }

  handleSelectAllChange() {
    const isChecked = this.selectAllCheckbox.checked;
    this.itemCheckboxes.forEach((cb) => {
      cb.checked = isChecked;
    });
    this.applyItemVisualStates();
    this.updateTotal();
    this.updateSelectAll();
    this.updateSubmitState();
  }

  handleInputChange(event) {
    if (!(event.target instanceof Element)) {
      return;
    }

    const article = event.target.closest('[data-reorder-item]');

    if (!article) {
      return;
    }

    if (
      event.target instanceof HTMLSelectElement &&
      event.target.classList.contains('form-field__select')
    ) {
      this.syncArticleVariant(article);
      this.updateLinePrice(article);
      this.updateTotal();
      return;
    }

    if (
      !(event.target instanceof HTMLInputElement) ||
      !event.target.classList.contains('input-qty__input')
    ) {
      return;
    }

    this.syncArticleQuantity(article);
    this.updateLinePrice(article);
    this.updateTotal();
  }

  handleArticleClick(event, article) {
    if (this.isSubmitting) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    if (event.target.closest('a, button, input, label, input-select')) {
      return;
    }

    const checkbox = article.querySelector('[data-reorder-item-checkbox]');

    if (!checkbox) {
      return;
    }

    checkbox.checked = !checkbox.checked;
    this.handleItemChange();
  }

  applyItemVisualStates() {
    this.itemArticles.forEach((article) => {
      const checkbox = article.querySelector('[data-reorder-item-checkbox]');
      const isSelected = Boolean(checkbox?.checked);
      article.classList.toggle('border-primary', isSelected);
      article.classList.toggle('border-border', !isSelected);

      const icon = article.querySelector('[data-reorder-selected-indicator]');

      if (icon) {
        icon.classList.toggle('hidden', !isSelected);
      }
    });
  }

  handleSubmitClick() {
    if (this.isSubmitting) return;
    this.reorder();
  }

  updateSubmitState() {
    if (!this.submitButton) {
      return;
    }

    const hasSelectedItems = this.getSelectedArticles().length > 0;
    const shouldDisable = this.isSubmitting || !hasSelectedItems;

    this.submitButton.disabled = shouldDisable;
    this.submitButton.setAttribute('aria-disabled', String(shouldDisable));
  }

  async reorder() {
    const selectedArticles = this.getSelectedArticles();

    if (selectedArticles.length === 0) {
      this.showError(REORDER_NO_ITEMS_MESSAGE);
      return;
    }

    const allItemsSelected =
      this.itemCheckboxes.length > 0 &&
      this.itemCheckboxes.every((checkbox) => checkbox.checked);

    if (!allItemsSelected) {
      const shouldContinue = window.confirm(this.partialConfirmMessage);

      if (!shouldContinue) {
        return;
      }
    }

    this.isSubmitting = true;
    window.LenszaAnalytics?.publish('reorder_started', {
      item_count: selectedArticles.length,
    });
    this.submitButton?.classList.add('loading');
    this.updateSubmitState();
    this.hideError();

    try {
      await this.clearCart();

      for (const article of selectedArticles) {
        const variantId = Number(article.dataset.variantId);
        const quantity = this.getArticleQuantity(article);
        const properties = this.parseProperties(article.dataset.properties);
        await this.addItemToCart(variantId, quantity, properties);
      }

      window.LenszaAnalytics?.publish('reorder_checkout_started', {
        item_count: selectedArticles.length,
      });
      window.location.href = '/checkout';
    } catch (_error) {
      this.showError(REORDER_CART_ERROR_MESSAGE);
      this.submitButton?.classList.remove('loading');
      this.isSubmitting = false;
      this.updateSubmitState();
    }
  }

  async clearCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Cart clear failed: ${response.status}`);
    }
  }

  async addItemToCart(variantId, quantity, properties) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(quantity));

    if (properties) {
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(
        data.description || `Add to cart failed: ${response.status}`,
      );
    }
  }

  parseProperties(raw) {
    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        return {};
      }
      return parsed;
    } catch {
      return {};
    }
  }

  initializeArticleVariants() {
    this.itemArticles.forEach((article) => {
      this.syncArticleVariant(article);
    });
  }

  getSelectedOptionValues(article) {
    const optionGroups = Array.from(
      article.querySelectorAll('[data-reorder-option-group]'),
    ).sort(
      (a, b) =>
        Number(a.dataset.optionPosition || 0) -
        Number(b.dataset.optionPosition || 0),
    );

    if (optionGroups.length === 0) {
      return [];
    }

    return optionGroups.map((group) => {
      const select = group.querySelector('select.form-field__select');
      return select?.value || '';
    });
  }

  getArticleVariants(article) {
    if (Array.isArray(article._reorderVariants)) {
      return article._reorderVariants;
    }

    const variantsScript = article.querySelector(
      '[data-reorder-variants-json]',
    );

    if (!variantsScript) {
      article._reorderVariants = [];
      return article._reorderVariants;
    }

    try {
      const variants = JSON.parse(variantsScript.textContent || '[]');
      article._reorderVariants = Array.isArray(variants) ? variants : [];
    } catch {
      article._reorderVariants = [];
    }

    return article._reorderVariants;
  }

  findMatchingVariant(variants, selectedOptions) {
    if (!Array.isArray(variants) || variants.length === 0) {
      return null;
    }

    if (selectedOptions.length === 0) {
      return variants[0] || null;
    }

    return (
      variants.find((variant) => {
        if (!Array.isArray(variant.options)) {
          return false;
        }

        return selectedOptions.every(
          (selectedValue, index) => variant.options[index] === selectedValue,
        );
      }) || null
    );
  }

  syncArticleVariant(article) {
    const selectedOptions = this.getSelectedOptionValues(article);

    if (selectedOptions.length === 0) {
      return;
    }

    const variants = this.getArticleVariants(article);
    const matchingVariant = this.findMatchingVariant(variants, selectedOptions);

    if (!matchingVariant) {
      return;
    }

    article.dataset.variantId = String(matchingVariant.id || '');
    article.dataset.price = String(Number(matchingVariant.price) || 0);
  }

  getArticleQuantity(article) {
    const quantityInput = article.querySelector('.input-qty__input');
    const quantityValue = Number(quantityInput?.value);
    const quantity = Number.isFinite(quantityValue)
      ? Math.max(1, quantityValue)
      : Number(article.dataset.quantity) || 1;

    article.dataset.quantity = String(quantity);
    return quantity;
  }

  syncArticleQuantity(article) {
    this.getArticleQuantity(article);
  }

  syncAllQuantities() {
    this.itemArticles.forEach((article) => {
      this.syncArticleQuantity(article);
    });
  }

  updateLinePrice(article) {
    const linePriceElement = article.querySelector('[data-reorder-line-price]');

    if (!linePriceElement) {
      return;
    }

    const unitPrice = Number(article.dataset.price) || 0;
    const quantity = this.getArticleQuantity(article);
    linePriceElement.textContent = this.formatMoney(unitPrice * quantity);
  }

  updateAllLinePrices() {
    this.itemArticles.forEach((article) => {
      this.updateLinePrice(article);
    });
  }

  formatMoney(cents) {
    return Shopify.formatMoney(cents, wakaTheme.moneyFormat);
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.classList.remove('hidden');
  }

  hideError() {
    if (!this.errorElement) return;
    this.errorElement.textContent = '';
    this.errorElement.classList.add('hidden');
  }
}

if (!customElements.get('reorder-products')) {
  customElements.define('reorder-products', ReorderProducts);
}
